import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Search, Car, Upload, CheckCircle, MapPin, Calendar, CreditCard, Key, Star, Shield } from 'lucide-react';

const HowItWorks = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const userSteps = [
    { icon: Search, title: '1. Busca tu auto ideal', description: 'Explora cientos de autos en tu ciudad. Filtra por tipo, precio, ubicación y más.', color: 'bg-blue-100 text-blue-600' },
    { icon: Calendar, title: '2. Selecciona fechas', description: 'Elige cuándo necesitas el auto. Mínimo 1 día, máximo 30 días.', color: 'bg-green-100 text-green-600' },
    { icon: Upload, title: '3. Sube tu licencia', description: 'Fotografía ambos lados de tu licencia de conducir vigente.', color: 'bg-purple-100 text-purple-600' },
    { icon: CreditCard, title: '4. Reserva sin cargo', description: 'Tu tarjeta será autorizada pero NO cargada hasta que el Mobilitier apruebe.', color: 'bg-orange-100 text-orange-600' },
    { icon: CheckCircle, title: '5. Espera aprobación', description: 'El Mobilitier revisará tu solicitud (usualmente en menos de 2 horas).', color: 'bg-pink-100 text-pink-600' },
    { icon: Key, title: '6. ¡Disfruta tu viaje!', description: 'Recoge el auto, documenta su estado con fotos y ¡a rodar!', color: 'bg-yellow-100 text-yellow-600' }
  ];

  const mobilitierSteps = [
    { icon: Car, title: '1. Publica tu auto', description: 'Sube 6 fotos de calidad, describe las características y fija tu precio por día.', color: 'bg-blue-100 text-blue-600' },
    { icon: Shield, title: '2. Conéctate a Stripe', description: 'Configura tu cuenta para recibir pagos de forma segura. Verificación en minutos.', color: 'bg-green-100 text-green-600' },
    { icon: Calendar, title: '3. Gestiona disponibilidad', description: 'Marca días disponibles/no disponibles. Pausa tu auto cuando quieras.', color: 'bg-purple-100 text-purple-600' },
    { icon: CheckCircle, title: '4. Aprueba reservas', description: 'Revisa solicitudes, verifica licencias de usuarios y aprueba/rechaza.', color: 'bg-orange-100 text-orange-600' },
    { icon: MapPin, title: '5. Entrega el auto', description: 'Coordina lugar de entrega, documenta estado con fotos (check-in).', color: 'bg-pink-100 text-pink-600' },
    { icon: Star, title: '6. Recibe tu pago', description: 'Al terminar el viaje, recibe el 80% del precio. Plataforma retiene 20%.', color: 'bg-yellow-100 text-yellow-600' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="bg-gradient-to-r from-[#000000] to-[#5B7FFF] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">¿Cómo funciona Mobility?</h1>
          <p className="text-xl max-w-3xl mx-auto opacity-90">
            Rentar o rentar tu auto nunca fue tan fácil. Conoce el proceso completo en minutos.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Para Usuarios</h2>
            <p className="text-lg text-gray-600">Renta el auto perfecto en 6 sencillos pasos</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {userSteps.map((step, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className={`w-16 h-16 ${step.color} rounded-full flex items-center justify-center mb-4`}>
                    <step.icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button size="lg" className="bg-[#000000] hover:bg-ink-700 text-white" onClick={() => navigate('/search')}>
              Buscar autos ahora
            </Button>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Para Mobilitiers</h2>
            <p className="text-lg text-gray-600">Genera ingresos rentando tu auto</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mobilitierSteps.map((step, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className={`w-16 h-16 ${step.color} rounded-full flex items-center justify-center mb-4`}>
                    <step.icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button size="lg" className="bg-[#bcef3c] hover:bg-[#a8d92e] text-gray-900 font-semibold" onClick={() => navigate('/become-mobilitier')}>
              Conviértete en Mobilitier
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
